import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'js/design_system/Button';
import { Heading } from 'js/design_system/Heading';
import { Icon } from 'js/design_system/Icon';
import { Modal, ModalContent } from 'js/design_system/Modal';
import { TextButton } from 'js/design_system/TextButton';
import trackClick from 'js/utils/trackClick';
import { loadRecordHit } from 'js/utils/loadRecordHit';
import {
  getImpressionId,
  ImpressionEntity,
  sendBatchImpressions,
} from 'js/utils/impressions/watchImpressions';

import { prepareTableForModal } from 'js/journals/article/ExpandableTable';

import { observeModalTables } from 'js/journals/article/tableOverflowFade';

const EXHIBIT_HASH_PREFIX = '#exhibits_';

type ArticleVisual = {
  label: string;
  target: HTMLElement;
  images: HTMLImageElement[];
  caption: Element | null;
  table: HTMLElement | null;
};

function FigureImage({
  image,
  label,
  preview = false,
}: {
  image?: HTMLImageElement;
  label: string;
  preview?: boolean;
}) {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [showOriginalImage, setShowOriginalImage] = useState(false);
  const thumbnailSrc =
    preview && !showOriginalImage && image?.dataset.thumbnailSrc;
  useEffect(() => {
    setImageLoadFailed(false);
    setShowOriginalImage(false);
  }, [image]);
  if (!image || imageLoadFailed) {
    return (
      <div
        className="sap-figures__missing-image"
        role="img"
        aria-label={`${label}: image unavailable`}
      >
        <Icon name="image" size={48} aria-hidden="true" />
        {!preview && <span>Image unavailable</span>}
      </div>
    );
  }
  return (
    <img
      src={thumbnailSrc || image.src}
      srcSet={thumbnailSrc ? image.dataset.thumbnailSrcset : undefined}
      decoding="async"
      alt={preview ? '' : image.alt || label}
      loading={preview ? 'lazy' : undefined}
      onError={() => {
        if (thumbnailSrc) setShowOriginalImage(true);
        else setImageLoadFailed(true);
      }}
    />
  );
}

function FigureCaption({ caption }: { caption: Element | null }) {
  const container = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!container.current || !caption) return undefined;
    const clone = caption.cloneNode(true) as Element;
    clone.removeAttribute('id');
    clone
      .querySelectorAll('[id]')
      .forEach((node) => node.removeAttribute('id'));
    container.current.replaceChildren(clone);
    return () => clone.remove();
  }, [caption]);
  return <div ref={container} className="sap-figures__caption" />;
}

function TableContent({ table }: { table: HTMLElement }) {
  const container = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!container.current) return undefined;
    const clone = prepareTableForModal(table);
    container.current.replaceChildren(clone);
    const cleanup = observeModalTables(clone);
    return () => {
      cleanup();
      clone.remove();
    };
  }, [table]);
  return (
    <div
      ref={container}
      className="sap__article-body sap__table-wrap--in-modal sap-figures__table"
    />
  );
}

type Analytics = {
  workId: number;
  publishedLetterId: number;
  loggedIn: boolean;
};

export default function FigureCarousel({
  analytics,
  quickEvaluationLayout = false,
}: {
  analytics?: Analytics | null;
  quickEvaluationLayout?: boolean;
}) {
  const track = (source: string, item?: ArticleVisual, method?: string) => {
    if (!analytics) return;
    trackClick({
      source,
      entityId: analytics.workId,
      extra: {
        published_letter_id: analytics.publishedLetterId,
        logged_in: analytics.loggedIn,
        item_type: item ? (item.table ? 'table' : 'figure') : undefined,
        label: item?.label,
        item_index: item ? items.indexOf(item) + 1 : undefined,
        item_id: item?.target.id || undefined,
        method,
        item_count: items.length,
      },
    });
  };
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<ArticleVisual[]>([]);
  const hasFigures = items.some((item) => !item.table);
  const hasTables = items.some((item) => item.table);
  const title =
    hasFigures && hasTables
      ? 'Figures & tables'
      : hasTables
        ? 'Tables'
        : 'Figures';
  const [active, setActive] = useState<number | null>(null);
  const strip = useRef<HTMLDivElement>(null);
  const modal = useRef<HTMLDialogElement>(null);
  const [scrollEdges, setScrollEdges] = useState({ start: true, end: true });

  useLayoutEffect(() => {
    const root = document.querySelector('#sap-article-body');
    const body = root?.querySelector('#article-body');
    if (!root || !body) return undefined;
    let figureCount = 0;
    let tableCount = 0;
    const articleItems = Array.from(
      root.querySelectorAll<HTMLElement>(
        '.sap__fig, .sap__table-wrap:has(table)',
      ),
    ).map((element) => {
      const isTable = element.matches('.sap__table-wrap');
      const number = isTable ? ++tableCount : ++figureCount;
      return {
        table: isTable && element.querySelector('table') ? element : null,
        target: element.querySelector<HTMLElement>(':scope > [id]') || element,
        label:
          element.querySelector('.sap__label')?.textContent?.trim() ||
          `${isTable ? 'Table' : 'Figure'} ${number}`,
        images: Array.from(
          element.querySelectorAll<HTMLImageElement>('img'),
        ).filter(
          (image) =>
            !image.closest('.sap__caption') && image.getAttribute('src'),
        ),
        caption: element.querySelector('.sap__caption'),
      };
    });
    if (!articleItems.length) return undefined;
    const mount = document.createElement('div');
    const destination = quickEvaluationLayout
      ? document.getElementById('sap-evaluation-figures')
      : body;
    (destination || body).prepend(mount);
    setHost(mount);
    setItems(articleItems);
    return () => mount.remove();
  }, [quickEvaluationLayout]);

  useEffect(() => {
    const openLinkedExhibit = () => {
      let id: string;
      try {
        id = window.location.hash.startsWith(EXHIBIT_HASH_PREFIX)
          ? decodeURIComponent(
              window.location.hash.slice(EXHIBIT_HASH_PREFIX.length),
            )
          : '';
      } catch {
        setActive(null);
        return;
      }
      const index = items.findIndex(
        (item) => item.target.id && item.target.id === id,
      );
      setActive(index === -1 ? null : index);
    };
    openLinkedExhibit();
    window.addEventListener('hashchange', openLinkedExhibit);
    return () => window.removeEventListener('hashchange', openLinkedExhibit);
  }, [items]);

  useLayoutEffect(() => {
    const element = strip.current;
    if (!element) return undefined;
    element.scrollLeft = 0;
    const update = () =>
      setScrollEdges({
        start: element.scrollLeft <= 1,
        end:
          element.scrollLeft + element.clientWidth >= element.scrollWidth - 1,
      });
    update();
    element.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => {
      element.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [host]);

  useEffect(() => {
    const element = strip.current;
    if (!analytics || !element || !items.length) return undefined;
    let cancelled = false;
    let observer: IntersectionObserver | undefined;
    const targets = [element, ...Array.from(element.children)];
    const seen = new Set<Element>();
    const observeVisible = () => {
      if (!document.hidden)
        targets.forEach((target) => {
          if (!seen.has(target)) observer?.observe(target);
        });
      else observer?.disconnect();
    };
    void loadRecordHit()
      .then(() => {
        const hitId = window.Aedu?.hit_data?.id;
        if (cancelled || !hitId) return;
        observer = new IntersectionObserver(
          (entries) => {
            if (document.hidden) return;
            const visible = entries.filter(
              (entry) =>
                entry.isIntersecting &&
                entry.intersectionRatio >= 0.5 &&
                !seen.has(entry.target),
            );
            if (!visible.length) return;
            sendBatchImpressions(
              visible.map(({ target }) => {
                seen.add(target);
                observer?.unobserve(target);
                const index = targets.indexOf(target) - 1;
                const item = items[index];
                return {
                  impression_id: getImpressionId(),
                  source: item
                    ? 'sap_figure_carousel_item_impression'
                    : 'sap_figure_carousel_impression',
                  entity_id: analytics.workId,
                  entity_type: ImpressionEntity.Work,
                  hit_id: hitId,
                  impression_time: new Date(),
                  extra: {
                    published_letter_id: analytics.publishedLetterId,
                    logged_in: analytics.loggedIn,
                    item_index: item ? index + 1 : undefined,
                    item_id: item?.target.id || undefined,
                    item_type: item
                      ? item.table
                        ? 'table'
                        : 'figure'
                      : undefined,
                    label: item?.label,
                    item_count: items.length,
                  },
                };
              }),
            );
          },
          { threshold: 0.5 },
        );
        observeVisible();
      })
      .catch(() => undefined);
    document.addEventListener('visibilitychange', observeVisible);
    return () => {
      cancelled = true;
      observer?.disconnect();
      document.removeEventListener('visibilitychange', observeVisible);
    };
  }, [analytics, host, items]);

  useLayoutEffect(() => {
    modal.current?.querySelector('.ds2-5-modal__container')?.scrollTo(0, 0);
    modal.current?.querySelector('.ds2-5-modal__content')?.scrollTo(0, 0);
  }, [active]);

  if (!host || !items.length) return null;
  const visual = active === null ? null : items[active];
  const selectExhibit = (index: number | null) => {
    setActive(index);
    const id = index === null ? null : items[index].target.id;
    if (!id && !window.location.hash.startsWith(EXHIBIT_HASH_PREFIX)) return;
    const hash = id ? `${EXHIBIT_HASH_PREFIX}${encodeURIComponent(id)}` : '';
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}${hash}`,
    );
  };
  const scroll = (direction: number) => {
    track('sap_figure_carousel_scroll');
    const element = strip.current;
    element?.scrollBy({
      left: direction * element.clientWidth,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    });
  };

  const navigate = (index: number, method: string) => {
    if (index < 0 || index >= items.length || index === active) return;
    track('sap_figure_carousel_navigate', items[index], method);
    selectExhibit(index);
  };

  const viewInArticle = (target: HTMLElement) => {
    selectExhibit(null);
    requestAnimationFrame(() => {
      target.tabIndex = -1;
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: target.id ? 'start' : 'center' });
      if (target.id) {
        window.history.pushState(null, '', `#${target.id}`);
      }
    });
  };

  return createPortal(
    <section className="sap-figures" aria-label="Figures and tables">
      <div className="sap-figures__header">
        <Heading as="h2" size="lg">
          {title} <span>({items.length})</span>
        </Heading>
        <div className="sap-figures__controls">
          <Button
            secondary
            size="sm"
            className="sap-figures__arrow"
            aria-label="Previous figures and tables"
            icon={
              <Icon
                name="chevron_left"
                size={quickEvaluationLayout ? 16 : 20}
              />
            }
            disabled={scrollEdges.start}
            onClick={() => scroll(-1)}
          />
          <Button
            secondary
            size="sm"
            className="sap-figures__arrow"
            aria-label="Next figures and tables"
            icon={
              <Icon
                name="chevron_right"
                size={quickEvaluationLayout ? 16 : 20}
              />
            }
            disabled={scrollEdges.end}
            onClick={() => scroll(1)}
          />
        </div>
      </div>
      <div className="sap-figures__strip" ref={strip}>
        {items.map((item, index) => (
          <Button
            secondary
            className="sap-figures__thumbnail"
            key={index}
            type="button"
            aria-label={`Enlarge ${item.label}`}
            onClick={() => {
              track('sap_figure_carousel_open', item, 'card');
              selectExhibit(index);
            }}
          >
            <div className="sap-figures__preview" aria-hidden="true">
              {item.table ? (
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  focusable="false"
                >
                  <rect x="3" y="4" width="18" height="16" rx="1" />
                  <path d="M3 9h18M3 14h18M9 9v11M15 9v11" />
                </svg>
              ) : (
                <FigureImage
                  image={item.images[0]}
                  label={item.label}
                  preview
                />
              )}
            </div>
            <span>{item.label}</span>
            <span className="sap-figures__card-caption">
              {item.caption?.textContent?.trim()}
            </span>
          </Button>
        ))}
      </div>
      {createPortal(
        <Modal
          ref={modal}
          className="sap-figures__modal"
          open={visual !== null}
          onClose={() => selectExhibit(null)}
          title={title}
          aria-label={`${title} viewer`}
          onKeyDown={(event) => {
            if ((event.target as HTMLElement).closest('.sap-figures__table'))
              return;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              event.preventDefault();
              const direction = event.key === 'ArrowLeft' ? -1 : 1;
              if (active !== null) navigate(active + direction, 'keyboard');
            }
          }}
        >
          <ModalContent
            onClick={(event) => {
              if (
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              )
                return;
              const anchor = (event.target as HTMLElement).closest('a');
              const href = anchor?.getAttribute('href');
              if (!href?.startsWith('#')) return;
              const target = document.getElementById(href.slice(1));
              if (!target) return;
              event.preventDefault();
              viewInArticle(target);
            }}
          >
            {visual && active !== null && (
              <>
                <div className="sap-figures__navigation">
                  <TextButton
                    icon="chevron_left"
                    aria-label="Previous item"
                    disabled={active === 0}
                    onClick={() => navigate(active - 1, 'previous')}
                  >
                    Previous
                  </TextButton>
                  <span aria-live="polite">
                    {active + 1} of {items.length}
                  </span>
                  <TextButton
                    iconRight="chevron_right"
                    aria-label="Next item"
                    disabled={active === items.length - 1}
                    onClick={() => navigate(active + 1, 'next')}
                  >
                    Next
                  </TextButton>
                </div>
                {visual.table ? (
                  <TableContent key={active} table={visual.table} />
                ) : (
                  <figure className="sap-figures__enlarged" key={active}>
                    {visual.images.length ? (
                      visual.images.map((image, index) => (
                        <FigureImage
                          key={index}
                          image={image}
                          label={visual.label}
                        />
                      ))
                    ) : (
                      <FigureImage label={visual.label} />
                    )}
                    <figcaption>
                      <strong>{visual.label}</strong>
                      <FigureCaption caption={visual.caption} />
                    </figcaption>
                  </figure>
                )}
                <TextButton
                  inline
                  iconRight="arrow_downward"
                  onClick={() => {
                    track('sap_figure_carousel_view_in_article', visual);
                    viewInArticle(visual.target);
                  }}
                >
                  View in article
                </TextButton>
              </>
            )}
          </ModalContent>
        </Modal>,
        document.body,
      )}
    </section>,
    host,
  );
}
